import classNames from 'classnames';
import { dot } from 'dot-object';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Button from 'reactstrap/lib/Button';
import Col from 'reactstrap/lib/Col';
import Collapse from 'reactstrap/lib/Collapse';
import Fade from 'reactstrap/lib/Fade';
import FormGroup from 'reactstrap/lib/FormGroup';
import Label from 'reactstrap/lib/Label';
import Row from 'reactstrap/lib/Row';
import Spinner from 'reactstrap/lib/Spinner';

import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';

import { loadCart, updateCartPaymentInformation } from '@ttstr/actions';
import { createPayPalOrder, queuePayPalOrderBackgroundUpdate } from '@ttstr/api/cart';
import { PaymentInformation, PaymentMethod, Cart } from '@ttstr/api';
import { AppState } from '@ttstr/reducers';
import { BIC_PATTERN, IBAN_PATTERN, useActions, useShallowEqualSelector } from '@ttstr/utils';

import Container from '../Container/Container';
import FormField from '../Form/FormField';
import ValidationAlert from '../Form/ValidationAlert';
import LoadingSpinner from '../Loading/LoadingSpinner';
import RadioList from '../RadioList/RadioList';
import RadioListItem from '../RadioList/RadioListItem';
import { useShopConfig } from '../ShopConfig/ShopConfigContext';

type PaymentDefaultValues = Partial<{
  payment_method_sid: string;
  payment_attributes: {
    account_owner: string;
    street_address: string;
    zip_city: string;
    bic: string;
    iban: string;
  };
}>;

const Payment: React.FC = () => {
  const { t } = useTranslation();
  const { cart, loading, cartReceived } = useShallowEqualSelector(mapStateToProps);

  const defaultValues: PaymentDefaultValues = React.useMemo(() => {
    if (!cart) return;
    return {
      payment_method_sid: cart.payment_method_sid,
      payment_attributes: {
        account_owner: cart.payment_attributes.account_owner,
        street_address: cart.payment_attributes.street_address,
        zip_city: cart.payment_attributes.zip_city,
        bic: cart.payment_attributes.bic,
        iban: cart.payment_attributes.iban,
      },
    };
  }, [cart]);

  if ((!cartReceived && loading) || !cart) {
    return <LoadingSpinner label={t(`LOADING.CART`)} />;
  }

  return (
    <PaymentForm
      availablePaymentMethods={cart.available_payment_methods}
      defaultValues={defaultValues}
      externalAlertMessage={cart.no_checkout_message}
    />
  );
};

interface PaymentFormProps {
  availablePaymentMethods: PaymentMethod[];
  defaultValues?: PaymentDefaultValues;
  externalAlertMessage?: string;
}

const PaymentFormComponent: React.FC<PaymentFormProps> = ({
  availablePaymentMethods,
  defaultValues,
  externalAlertMessage,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { updateCartPaymentInformation } = useActions(mapDispatchToProps);
  const formContext = useForm<PaymentInformation>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });
  const { control, handleSubmit, formState, errors, setError, trigger } = formContext;
  const { isValid, isSubmitting } = formState;

  const currentPaymentMethodSid = useWatch<string>({ control, name: 'payment_method_sid' });

  const config = useShopConfig();
  const ppClientId = config.payPal2?.clientId;
  const ppBnCode = config.payPal2?.bnCode;
  const [paypal2IsApproved, setPaypal2IsApproved] = React.useState(false);
  const { loadCart } = useActions(mapDispatchToProps);

  const onSubmit = async (values: PaymentInformation) => {
    setPaypal2IsApproved(false);
    const currentPaymentMethod = availablePaymentMethods.find((m) => m.sid === values.payment_method_sid);

    const updatedCart: Cart = await updateCartPaymentInformation({
      ...values,
      // add payment method id to the payload
      payment_method_id: currentPaymentMethod?.id,
    });
    if (!updatedCart.valid) {
      Object.entries(dot(updatedCart.errors)).forEach(([field, errorMessage]: [string, string]) => {
        setError(field, { type: 'DEFAULT', message: errorMessage });
      });
      return;
    }

    history.push('/checkout/confirm');
  };

  const currentPaymentMethod = React.useMemo(
    () => availablePaymentMethods.find((m) => m.sid === currentPaymentMethodSid),
    [availablePaymentMethods, currentPaymentMethodSid]
  );

  return (
    <FormProvider {...formContext}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <Container className="mb-5">
            <Row>
              <Col lg="6">
                <div className="tt-sticky-top">
                  <FormGroup tag="fieldset" className="mb-0">
                    <legend className="legend--payment-method">{t(`CHECKOUT.PAYMENT_METHOD`)}</legend>
                    <Label>{t(`CHECKOUT.PAYMENT_METHOD_DESCRIPTION`)}</Label>
                    <FormGroup className="mt-2">
                      <RadioList hasErrors={Boolean(errors.payment_method_sid)} className="payment-method-list">
                        {availablePaymentMethods.map((paymentMethod, index) => {
                          return (
                            <RadioListItem
                              key={paymentMethod.sid}
                              iconUrl={paymentMethod.icon}
                              title={paymentMethod.title}
                              inputId={`payment_method_${paymentMethod.sid}`}
                              name="payment_method_sid"
                              value={paymentMethod.sid}
                              price={Number(paymentMethod.price)}
                              autoFocus={index === 0}
                            ></RadioListItem>
                          );
                        })}
                      </RadioList>
                      {errors.payment_method_sid && (
                        <div className="invalid-feedback">
                          {t(`FORM.VALIDATION.${errors.payment_method_sid.type.toUpperCase()}`)}
                        </div>
                      )}
                    </FormGroup>
                  </FormGroup>
                </div>
              </Col>
              <Col lg="6">
                <Collapse isOpen={Boolean(currentPaymentMethodSid)}>
                  <legend className="legend--payment-method-additional">
                    {t(`CHECKOUT.PAYMENT_METHOD_ADDITIONAL`)}
                  </legend>
                  <p>{currentPaymentMethod && currentPaymentMethod.description}</p>
                  {currentPaymentMethodSid === 'paypal2' && paypal2IsApproved && (
                    <div className="alert alert-success">
                      <p>{t('CHECKOUT.PAYPAL2_APPROVED')}</p>
                      <p className="font-weight-bold mb-0">{t('CHECKOUT.PAYPAL2_APPROVED_2')}</p>
                    </div>
                  )}
                  {currentPaymentMethodSid === 'paypal2' && !paypal2IsApproved && (
                    <PayPalScriptProvider
                      options={{
                        clientId: ppClientId,
                        dataPartnerAttributionId: ppBnCode,
                        components: 'buttons',
                        currency: 'EUR',
                        disableFunding: 'sepa,card,credit,paylater,bancontact,blik,ideal',
                        commit: false,
                      }}
                    >
                      <PayPalButtons
                        createOrder={async () => {
                          const ppOrder = await createPayPalOrder();
                          await loadCart(); // dispatch action foo to disable cart menu and checkout nav
                          return ppOrder.order_id;
                        }}
                        onError={(err) => {
                          console.error(err);
                        }}
                        onApprove={async () => {
                          await queuePayPalOrderBackgroundUpdate();
                          setPaypal2IsApproved(true);
                        }}
                      />
                    </PayPalScriptProvider>
                  )}
                  {currentPaymentMethodSid === 'sepa' && (
                    <Fade>
                      <FormGroup tag="fieldset">
                        <FormField
                          type="text"
                          label={t('CUSTOMER.ACCOUNT_OWNER')}
                          name="payment_attributes.account_owner"
                          autoComplete="billing name"
                          validationOptions={{ required: true }}
                        />
                        <FormField
                          type="text"
                          label={t('CUSTOMER.STREET')}
                          name="payment_attributes.street_address"
                          autoComplete="billing street-address"
                          validationOptions={{ required: true }}
                        />
                        <FormField
                          type="text"
                          label={t('CUSTOMER.ZIP_CITY')}
                          name="payment_attributes.zip_city"
                          autoComplete="billing address-level2"
                          validationOptions={{ required: true }}
                        />
                        <FormField
                          type="text"
                          label={t('CUSTOMER.IBAN')}
                          name="payment_attributes.iban"
                          // eslint-disable-next-line react/jsx-no-literals
                          placeholder="DEXX YYYY YYYY ZZZZ ZZZZ ZZ"
                          onChange={(e) => {
                            if (typeof e.target.value === 'string') {
                              e.target.value = e.target.value.replace(/\s/g, '');
                              // @ts-ignore
                              trigger('payment_attributes.iban');
                            }
                          }}
                          validationOptions={{
                            required: true,
                            validate: {
                              'CUSTOM.INVALID_IBAN_FORMAT': (value: string) => IBAN_PATTERN.test(value),
                            },
                          }}
                        />
                        <FormField
                          type="text"
                          label="BIC"
                          name="payment_attributes.bic"
                          // eslint-disable-next-line react/jsx-no-literals
                          placeholder="XXXXDEYYYY"
                          onChange={(e) => {
                            if (typeof e.target.value === 'string') {
                              e.target.value = e.target.value.replace(/\s/g, '');
                              // @ts-ignore
                              trigger('payment_attributes.bic');
                            }
                          }}
                          validationOptions={{
                            required: true,
                            validate: {
                              'CUSTOM.INVALID_BIC_FORMAT': (value: string) => BIC_PATTERN.test(value),
                            },
                          }}
                        />
                      </FormGroup>
                    </Fade>
                  )}
                </Collapse>
              </Col>
            </Row>
          </Container>
          <div
            className={classNames('sticky-bottom when-valid', {
              'is-valid': currentPaymentMethodSid === 'paypal2' ? paypal2IsApproved : isValid,
            })}
          >
            <Container className="pt-2 mt-4">
              <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-md-center">
                <ValidationAlert className="mb-2" externalAlertMessage={externalAlertMessage} />
                <Button
                  className="mb-2"
                  type="submit"
                  size="lg"
                  color="primary"
                  disabled={
                    isSubmitting ||
                    !isValid ||
                    !!externalAlertMessage ||
                    (currentPaymentMethodSid === 'paypal2' && !paypal2IsApproved)
                  }
                >
                  <span className="d-flex justify-content-center align-items-center">
                    {t('CHECKOUT.COMPLETE_ORDER')}
                    <Spinner color="light" size="sm" className={isSubmitting ? 'ml-2 d-inline-block' : 'd-none'} />
                  </span>
                </Button>
              </div>
            </Container>
          </div>
        </fieldset>
      </form>
    </FormProvider>
  );
};
const PaymentForm = React.memo(PaymentFormComponent);

const mapStateToProps = (state: AppState) => {
  const { cart, loading, cartReceived } = state.Cart;
  return {
    cart,
    loading,
    cartReceived,
  };
};

const mapDispatchToProps = {
  loadCart,
  updateCartPaymentInformation,
};

export default React.memo(Payment);
